import type { TenderItem } from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { fmtMoney, fmtNum } from '@shared/lib/smetaLabels';
import { CodeCell } from './CodeCell';
import { ExclusionCell } from './ExclusionCell';
import { ItemRow, MutedMargin, Table, ThRight } from './styled';

interface ItemsTableProps {
  items: TenderItem[];
  estimateId: string;
  region: string | null;
  objectType: string | null;
}

/**
 * Вошла ли позиция в «Итого» в текущих ценах: ✓ — да; «в базисе» — норматив
 * найден, но индекс пересчёта не применён; «—» — не посчитана.
 */
function coverageLabel(it: TenderItem): string {
  if (it.covered) return '✓';
  if (it.current_total === null && it.norm_total_pz !== null) return 'в базисе';
  return '—';
}

/** Таблица сформированного ЛСР (базисные и текущие ПЗ по позициям). */
export function ItemsTable({
  items,
  estimateId,
  region,
  objectType,
}: ItemsTableProps) {
  const { can_write } = useAgentPermission('smeta');
  if (items.length === 0) {
    return <MutedMargin>Позиции не сформированы.</MutedMargin>;
  }
  return (
    <Table>
      <thead>
        <tr>
          <th>№</th>
          <th>Шифр</th>
          <th>Наименование</th>
          <th>Ед.</th>
          <ThRight>Кол-во</ThRight>
          <ThRight>ПЗ ед., базис 2001</ThRight>
          <ThRight>ПЗ всего, базис 2001</ThRight>
          <ThRight>Тек. стоимость</ThRight>
          <th>В итоге</th>
          <th>В расчёте</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <ItemRow key={it.id} $excluded={it.excluded}>
            <td className="num">{it.row_no ?? '—'}</td>
            <td>
              <CodeCell
                item={it}
                estimateId={estimateId}
                canWrite={can_write}
                region={region}
                objectType={objectType}
              />
            </td>
            <td>{it.name}</td>
            <td>{it.unit ?? '—'}</td>
            <td className="num">{fmtNum(it.qty)}</td>
            <td className="num">{fmtMoney(it.norm_unit_pz)}</td>
            <td className="num">{fmtMoney(it.norm_total_pz)}</td>
            <td className="num">{fmtMoney(it.current_total)}</td>
            <td>{coverageLabel(it)}</td>
            <td>
              <ExclusionCell item={it} estimateId={estimateId} canWrite={can_write} />
            </td>
          </ItemRow>
        ))}
      </tbody>
    </Table>
  );
}
